import {
  CaseStatus,
  CaseStatusForNotification,
  CustomerCaseStatus,
  OrganType,
  ResourceLogisticsStatus,
  RoutePlanStatus,
} from '@/api/apis/case';

export enum NotificationMessageType {
  CaseStatusUpdated = 'CaseStatusUpdated',
  RoutePlanningStatusUpdated = 'RoutePlanningStatusUpdated',
  CaseResourceLogisticsStatusUpdated = 'CaseResourceLogisticsStatusUpdated',
  Generic = 'Generic',
  RoutePlanningStarted = 'RoutePlanningStarted',
  RoutePlanningCompleted = 'RoutePlanningCompleted',
  RouteChange = 'RouteChange',
  CaseBulkOperationPrimaryElevation = 'CaseBulkOperationPrimaryElevation',
  CaseBulkSwapOperationCompleted = 'CaseBulkSwapOperationCompleted',
  CaseBulkAssignOperationCompleted = 'CaseBulkAssignOperationCompleted',
  CaseBulkDetachOperationCompleted = 'CaseBulkDetachOperationCompleted',
  ClinicalDataUpdated = 'ClinicalDataUpdated',
}

export enum NotificationToastMessageType {
  CaseCreated = 'CaseCreated',
  CaseStatusUpdated = 'CaseStatusUpdated',
  RoutePlanningStatusUpdated = 'RoutePlanningStatusUpdated',
  CaseResourceLogisticsStatusUpdated = 'CaseResourceLogisticsStatusUpdated',
  CaseResourceUnassigned = 'CaseResourceUnassigned',
  CaseResourceAssigned = 'CaseResourceAssigned',
  OrganAcceptanceCriteriaUpdated = 'OrganAcceptanceCriteriaUpdated',
  OrganApprovalUpdated = 'OrganApprovalUpdated',
  CaseDetailsUpdated = 'CaseDetailsUpdated',
  ResourceStatusUpdated = 'ResourceStatusUpdated',
  TBD = 'TBD',
  MAOTripCreationFailed = 'MAOTripCreationFailed',
  CaseResourceReserved = 'CaseResourceReserved',
  CaseReviewUpdated = 'CaseReviewUpdated',
  CaseRouteUpdated = 'CaseRouteUpdated',
  RoutePlanningStarted = 'RoutePlanningStarted',
  RoutePlanningCompleted = 'RoutePlanningCompleted',
  CaseQuoteRejectedInternally = 'CaseQuoteRejectedInternally',
  CaseQuoteCreated = 'CaseQuoteCreated',
  CaseQuoteApprovedByTxCenter = 'CaseQuoteApprovedByTxCenter',
  CaseQuoteRejectedByTxCenter = 'CaseQuoteRejectedByTxCenter',
  CaseQuoteRecalledByTxCenter = 'CaseQuoteRecalledByTxCenter',
  CaseQuoteSentToTxCenter = 'CaseQuoteSentToTxCenter',
  RandomizationCompleted = 'RandomizationCompleted',
  RandomizationReady = 'RandomizationReady',
  TrialCaseUpdated = 'TrialCaseUpdated',
  CaseResourceInvitationDeclined = 'CaseResourceInvitationDeclined',
  CaseQuoteApprovedInternally = 'CaseQuoteApprovedInternally',
  CaseQuoteApprovedTxCenterProvider = 'CaseQuoteApprovedTxCenterProvider',
  NOPServiceApproval = 'NOPServiceApproval',
  UpdateOrganAcceptanceCriteria = 'UpdateOrganAcceptanceCriteria',
  OrganApprovalAccepted = 'OrganApprovalAccepted',
  OrganApprovalRejected = 'OrganApprovalRejected',
  CaseQuoteReceivedByTxCenter = 'CaseQuoteReceivedByTxCenter',
  CaseBulkOperationPrimaryElevation = 'CaseBulkOperationPrimaryElevation',
  CaseBulkSwapOperationCompleted = 'CaseBulkSwapOperationCompleted',
  CaseBulkAssignOperationCompleted = 'CaseBulkAssignOperationCompleted',
  CaseBulkDetachOperationCompleted = 'CaseBulkDetachOperationCompleted',
  Generic = 'Generic',
  ClinicalDataUpdated = 'ClinicalDataUpdated',
  UnallocatedClinicalRequestReviewNeeded = 'UnallocatedClinicalRequestReviewNeeded',
}

export type SchedulerNotificationData = {
  Month?: string | null;
  Year?: string | null;
  PeriodStart?: string | null;
  PeriodEnd?: string | null;
  SchedulerNotificationType: string;
  SchedulerType: string;
  UserId: string;
  UserFullName: string;
};

export type NotificationDataStatus =
  | CaseStatus
  | RoutePlanStatus
  | ResourceLogisticsStatus
  | CustomerCaseStatus
  | CaseStatusForNotification;

export type BulkAssignmentNotificationData = {
  InitiatorName?: string;
  DetachedUserName?: string;
  AssignedUserName?: string;
  RoleName?: string;
  SuccessfulCases?: string;
  FailedCases?: string;
  FailedCasesWithCertificationReason?: string;
  IsAssignedAsPrimary?: 'True' | 'False';
};

export type BulkNotificationCaseData = {
  caseId: string;
  caseName: string;
};

export type CaseNotificationData = {
  NewStatus?: NotificationDataStatus;
  OldStatus?: NotificationDataStatus;
  LogisticsStatus?: NotificationDataStatus;
  txcCode?: string;
  CustomerText?: string | null;
  RouteLogId?: string;
  TxCenterId?: string;
  OpoOrganizationId?: string;
} & BulkAssignmentNotificationData;

export type BaseNotificationType = {
  id?: string;
  heading: string;
  eventText: string;
  createdAt: string;
};

export type SchedulerNotificationType = {
  category: 'Scheduler';
  data: SchedulerNotificationData;
} & BaseNotificationType;

export type CaseNotificationType = {
  category: OrganType;
  data: CaseNotificationData;
  caseId: string | null;
} & BaseNotificationType;

export type NotificationCategories = SchedulerNotificationType | CaseNotificationType;

export type NotificationType<T = CaseNotificationData> = {
  category: string;
  data: T;
  caseId: string | null;
  notificationType: NotificationMessageType;
  body: string;
  toastType: NotificationToastMessageType;
} & BaseNotificationType;

export type NotificationToastType = {
  category: string;
  data: CaseNotificationData;
  caseId?: string;
  body: string;
  toastType: NotificationToastMessageType;
  title: string;
} & BaseNotificationType;
